import type { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { useTranslations } from "@ui/providers/useTranslations";

import { UiWarningIcon } from "@jti/brand-icons/ui-warning";
import { Checkbox } from "@jti/core-components/components/Checkbox";

import styles from "./LoyaltyContactFormCheckboxSection.module.scss";

type CheckboxData = {
  name: string;
  label: string;
  value?: boolean;
};

export type LoyaltyContactFormCheckboxSectionProps = {
  required?: boolean;
  sectionName: string;
  sectionTitle: string;
  checkboxesData: CheckboxData[];
  validationErrorMessage?: string;
  type: "singleChoice" | "multipleChoice";
  titleAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
};

export const LoyaltyContactFormCheckboxSection: FC<LoyaltyContactFormCheckboxSectionProps> = ({
  sectionName,
  sectionTitle,
  checkboxesData,
  required = false,
  validationErrorMessage,
  type = "multipleChoice",
  titleAs: TitleTag = "h4",
}) => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();

  const { __ } = useTranslations();

  const isMultipleChoice = type === "multipleChoice";
  const isCheckboxError = Boolean(errors?.[sectionName]);
  const isOptional = !required;
  const optionalLabelText = isOptional ? __("form_fields.optional") : "";

  const handleGetNewSectionValue = (isValueAvailable: boolean, answers: string[]) => {
    if (isValueAvailable) {
      return {
        question: sectionTitle,
        answers,
      };
    }
  };

  const handleMultipleChoiceSelection = ({ label, value }: CheckboxData) => {
    const prevAnswers = getValues(sectionName)?.answers ?? [];
    const newAnswers: string[] = value ? [...prevAnswers, label] : prevAnswers.filter((item: string) => item !== label);

    return handleGetNewSectionValue(newAnswers.length > 0, newAnswers);
  };

  const handleSingleChoiceSelection = ({ label, value, name }: CheckboxData) => {
    checkboxesData.forEach((item) => {
      if (item.name !== name) {
        setValue(item.name, { checked: false });
      }
    });

    return handleGetNewSectionValue(value ?? false, [label]);
  };

  const handleChangeValue = (checkboxData: CheckboxData) => {
    const getNewDataForSectionTitle = isMultipleChoice ? handleMultipleChoiceSelection : handleSingleChoiceSelection;
    const newSectionData = getNewDataForSectionTitle(checkboxData);

    setValue(sectionName, newSectionData, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <div className={styles.container}>
      <TitleTag className={styles.dialogTitle}>
        {sectionTitle} {optionalLabelText}
      </TitleTag>
      <div className={styles.checkboxesWrapper}>
        {checkboxesData.map(({ label, name }) => (
          <Controller
            key={name}
            name={name}
            control={control}
            render={({ field }) => (
              <Checkbox
                key={label}
                label={label}
                status={isCheckboxError ? "error" : "default"}
                checked={field.value?.checked ?? false}
                name={field.name}
                onBlur={field.onBlur}
                ref={field.ref}
                onCheckedChange={(checked) => {
                  field.onChange({ ...field.value, checked });
                  handleChangeValue({ label, value: checked, name });
                }}
              />
            )}
          />
        ))}
        {isCheckboxError && (
          <div className={styles.errorWrapper}>
            <UiWarningIcon className={styles.errorIcon} />
            <p className={styles.errorMessage}>{validationErrorMessage}</p>
          </div>
        )}
      </div>

      <Controller
        name={sectionName}
        control={control}
        render={({ field }) => <input type="hidden" {...field} />}
        rules={{
          required: {
            value: required,
            message: validationErrorMessage ?? "",
          },
        }}
      />
    </div>
  );
};
